import React from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { IAdminUser, useAdminUserRecentPageViewsQuery } from '../../api/admin';
import { formatDateTime } from '../../utils/dates';

const RECENT_LIMIT = 10;

interface Props {
	/** The user whose activity to show; null closes the dialog. */
	user: IAdminUser | null;
	onClose: () => void;
}

/** Quick peek at a user's last few page views without leaving the users list. */
export default function RecentActivityDialog({ user, onClose }: Props) {
	const navigate = useNavigate();
	const { data: pageViews, isLoading } = useAdminUserRecentPageViewsQuery(user?.id ?? 0, RECENT_LIMIT);

	return (
		<Dialog open={!!user} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			<DialogTitle>Recent activity — {user?.full_name ?? user?.email}</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
					Last web activity: <strong>{formatDateTime(user?.last_page_view)}</strong> · Last MCP call:{' '}
					<strong>{formatDateTime(user?.last_mcp_call)}</strong>
				</Typography>
				{isLoading ? (
					<Stack spacing={1}>
						<Skeleton height={40} />
						<Skeleton height={40} />
						<Skeleton height={40} />
					</Stack>
				) : pageViews && pageViews.length > 0 ? (
					<List dense disablePadding>
						{pageViews.map((pv, i) => (
							<ListItem key={`${pv.created_at}-${i}`} disableGutters divider={i < pageViews.length - 1}>
								<ListItemText
									primary={
										<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
											<Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
												{pv.page}
											</Typography>
											{pv.info && <Chip label={pv.info} size="small" variant="outlined" />}
										</Stack>
									}
									secondary={formatDateTime(pv.created_at)}
									secondaryTypographyProps={{ variant: 'caption' }}
								/>
							</ListItem>
						))}
					</List>
				) : (
					<Typography variant="body2" color="text.disabled">
						No page views recorded.
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
				<Button
					variant="contained"
					onClick={() =>
						user &&
						navigate(`/admin/users/${user.id}`, { state: { userEmail: user.email, userName: user.full_name } })
					}
				>
					Open user page
				</Button>
			</DialogActions>
		</Dialog>
	);
}
